"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import portrait from "@/public/animatedAssets/leaderboards-rank2.jpg";
import sendAltFilled from "@/public/icons/Send--alt--filled.svg";

const chips = [
  "TRADING",
  "PLATFORM UPDATES",
  "TOOLS",
  "SCALPING SETUPS",
  "RISK MANAGEMENT",
  "COUNTER STRIKE SETUP",
  "MORE..."
];

type State = "collapsed" | "expanded";

export const AnimatedAsset1 = ({
  className = "",
  language,
}: {
  className?: string;
  language: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const barVariants = {
    collapsed: {y: 0, boxShadow: "0 0 0 rgba(0,0,0,0)"},
    expanded: {y: -2, boxShadow: "0 6px 16px rgba(0,0,0,.12)", transition: ease},
  };

  const outlineVariants = {
    collapsed: {opacity: 0, top: -10, right: -10, bottom: -10, left: -10, transition: ease},
    expanded: {opacity: 1, top: -4, right: -4, bottom: -4, left: -4, transition: {...ease, duration: 0.55}},
  };

  const chipVariants = {
    collapsed: (i: number) => ({
      opacity: i < 3 ? 1 : 0,
      y: 0,
      transition: {...ease, duration: 0.25},
    }),
    expanded: (i: number) => ({
      opacity: 1,
      y: -6,
      transition: {...ease, delay: i * 0.06},
    }),
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="w-[min(46.75rem,calc(100%-4rem))] bg-white rounded-full flex items-center justify-between p-[.75rem] relative"
        variants={barVariants}
        animate={controls}
        initial="collapsed"
      >
        <motion.div
          className="absolute rounded-full border border-[#D9F9B9] pointer-events-none"
          variants={outlineVariants}
          animate={controls}
          initial="collapsed"
        />

        <div className="flex items-center gap-[2rem]">
          <Image
            className="!w-[6rem] aspect-square object-cover rounded-full"
            src={portrait}
            quality={100}
            alt="portrait"
          />
          <p className="mb-0 leading-none text-black text-[2.25rem]">{language === "en" ? "Send question..." : "Frage stellen..."}</p>
        </div>

        <div className="bg-[#D9F9B9] rounded-full flex items-center gap-[.75rem] h-[6rem] px-[2rem]">
          <p className="mb-0 leading-none text-black/80 font-medium">{language === "en" ? "Send" : "Senden"}</p>
          <Image
            className="!w-[2.25rem]"
            src={sendAltFilled}
            alt="cursor icon"
          />
        </div>
      </motion.div>

      <div className="w-[min(46.75rem,calc(100%-4rem))] flex flex-wrap gap-[.875rem] mt-[2rem]">
        {chips.map((text, i) => (
          <motion.p
            key={text}
            custom={i}
            variants={chipVariants}
            animate={controls}
            initial="collapsed"
            className="mb-0 bg-[#D9F9B9]/10 border border-dashed border-[#D9F9B9]/30 rounded-full px-[2rem] text-[1.75rem] text-[#D9F9B9] h-[4.25rem] flex items-center justify-center font-geistmono leading-none"
          >
            {text}
          </motion.p>
        ))}
      </div>
    </div>
  );
};